import { useEffect, useRef, useState, useCallback } from "react";
import { scrollState, onPhaseChange } from "../state/scroll";
import { useContent } from "../state/siteContent";

const TOTAL_FRAMES = 240;

// Two resolution tiers of the same 240-frame construction timelapse:
//   /frames/     1600x900  (~23MB total) — desktop
//   /frames-sm/   800x450  (~7MB total)  — mobile/low-power
// The construction window is never wider than ~1000px even on desktop,
// and on phones it's full-width-stacked under the text at ~400-500px —
// so mobile devices were downloading and decoding 4x more pixel data
// than they could ever display. This was a major contributor to the
// construction section feeling laggy on Android.
function pickFrameTier() {
  if (typeof window === "undefined") return "frames";
  const isSmall = window.innerWidth < 768;
  const isTouch = /android|iphone|ipad|ipod/i.test(navigator.userAgent);
  return isSmall || isTouch ? "frames-sm" : "frames";
}
const FRAME_TIER = pickFrameTier();
const FRAME_PATH = (i) => `/${FRAME_TIER}/frame_${String(i).padStart(4, "0")}.webp`;

// Scroll positions at which each stage becomes active. The admin can edit the
// stage copy but the count/timing of the build storyboard is fixed at 5.
const STAGE_AT = [0.0, 0.2, 0.45, 0.7, 0.92];

export default function ConstructionPhase() {
  const { home } = useContent();
  // Map editable stage copy onto the fixed timing track.
  const STAGES = (home.stages || []).slice(0, STAGE_AT.length).map((s, i) => ({
    at: STAGE_AT[i],
    label: s.label,
    desc: s.desc,
    headline: [s.h1, s.h2],
    body: s.body,
    stat: { value: s.statValue, caption: s.statCaption },
  }));

  const canvasRef = useRef(null);
  const sectionRef = useRef(null);
  const framesRef = useRef([]);
  const currentFrameRef = useRef(0);
  const targetFrameRef = useRef(0);
  const rafRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(scrollState.phase);

  useEffect(() => onPhaseChange(setPhase), []);

  // Lenis runs freely through ConstructionPhase. The updateScroll RAF reads
  // getBoundingClientRect().top which reflects the Lenis-animated visual
  // position correctly (Lenis uses window.scrollTo so window.scrollY and
  // rect.top stay in sync). Stopping Lenis here blocked its wheel-event
  // interception, preventing window.scrollY from changing and freezing the
  // frame scrubber at frame 1.

  useEffect(() => {
    // Two-tier preload to avoid the main-thread spike from kicking off
    // all 240 image fetches at once (was causing the rAF "took 158ms"
    // violation in the console).
    //
    //   PRIORITY: first 30 frames load immediately so the section can
    //             paint as soon as the user reaches it.
    //   REST:     remaining 210 frames are queued via
    //             requestIdleCallback so they only fetch when the
    //             browser would otherwise be idle.
    //
    // We mark the canvas "loaded" as soon as the priority batch is in;
    // the rest stream in behind. Drawing falls back to the closest
    // already-loaded frame in drawFrame() so unloaded frames don't
    // show black — they show the previous one until they arrive.
    const PRIORITY_COUNT = 30;
    const imgs = new Array(TOTAL_FRAMES);
    framesRef.current = imgs;

    let priorityLoaded = 0;
    const onPriorityDone = () => {
      priorityLoaded++;
      if (priorityLoaded === PRIORITY_COUNT) setLoaded(true);
    };

    for (let i = 0; i < PRIORITY_COUNT; i++) {
      const img = new Image();
      img.src = FRAME_PATH(i + 1);
      img.onload = onPriorityDone;
      img.onerror = onPriorityDone;
      imgs[i] = img;
    }

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    const cancelIdle = window.cancelIdleCallback || clearTimeout;
    let nextIdx = PRIORITY_COUNT;
    let idleHandle;

    const loadNext = () => {
      const start = performance.now();
      while (nextIdx < TOTAL_FRAMES && performance.now() - start < 4) {
        const i = nextIdx++;
        const img = new Image();
        img.src = FRAME_PATH(i + 1);
        imgs[i] = img;
      }
      if (nextIdx < TOTAL_FRAMES) idleHandle = idle(loadNext);
    };
    idleHandle = idle(loadNext);

    return () => { if (idleHandle) cancelIdle(idleHandle); };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      // Cap the backing-store resolution at the source frame width
      // (1920 for the current export). Going larger than source forces
      // the GPU to upscale a 1920px image onto a 3840px canvas on hi-DPI
      // displays, which adds visible softness. Capping at source means
      // every output pixel maps to <= 1 source pixel — sharper result.
      // When frames are re-exported at higher resolution this cap rises
      // automatically (it reads naturalWidth at draw time).
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      drawFrame(currentFrameRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [loaded, phase]);

  const drawFrame = useCallback((frameIdx) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    // With staggered preload, the user can scroll into a frame range
    // that hasn't downloaded yet. Walk backward to the nearest already-
    // loaded frame so the canvas shows the most recent valid image
    // instead of going blank — looks like the build paused for a
    // moment, which is much better than a black flash.
    let idx = Math.floor(frameIdx);
    let img = framesRef.current[idx];
    while ((!img || !img.complete || !img.naturalWidth) && idx > 0) {
      idx--;
      img = framesRef.current[idx];
    }
    if (!img || !img.complete || !img.naturalWidth) return;

    const iw = img.naturalWidth, ih = img.naturalHeight;
    let cw = canvas.width, ch = canvas.height;

    // If the canvas backing store is larger than the source image, the
    // browser will upscale and the result will look soft. Shrink the
    // backing store to match the source on this draw — the CSS size is
    // unchanged so the visible element stays the same size, but each
    // output pixel now maps to one source pixel.
    const aspect = iw / ih;
    const canvasAspect = cw / ch;
    const fitW = canvasAspect > aspect ? ch * aspect : cw;
    const fitH = canvasAspect > aspect ? ch : cw / aspect;
    if (fitW > iw || fitH > ih) {
      const limit = Math.min(iw / fitW, ih / fitH);
      cw = Math.round(cw * limit);
      ch = Math.round(ch * limit);
      if (canvas.width !== cw) canvas.width = cw;
      if (canvas.height !== ch) canvas.height = ch;
    }

    const scale = Math.max(cw / iw, ch / ih);

    const p = frameIdx / (TOTAL_FRAMES - 1);
    const finalBoost = Math.max(0, (p - 0.85) / 0.15);
    const zoomScale = scale * (1 + finalBoost * 0.04);
    const brightness = 1 + finalBoost * 0.08;

    const dw = iw * zoomScale, dh = ih * zoomScale;
    const dx = (cw - dw) / 2, dy = (ch - dh) / 2;

    // High-quality resampling filter. Default is "low" (box filter).
    // "high" uses Lanczos on Chromium, which gives noticeably sharper
    // edges on any scale ratio.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.clearRect(0, 0, cw, ch);
    ctx.filter = `brightness(${brightness})`;
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.filter = "none";
  }, []);

  useEffect(() => {
    if (!loaded || phase !== "PLOT") return;

    let raf;
    let lastP = -1;

    const updateScroll = () => {
      const section = sectionRef.current;
      if (!section) {
        raf = requestAnimationFrame(updateScroll);
        return;
      }
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = rect.height;
      const vh = window.innerHeight;
      const pinDistance = sectionHeight - vh;

      let p;
      if (sectionTop > 0) p = 0;
      else if (sectionTop < -pinDistance) p = 1;
      else p = -sectionTop / pinDistance;

      const BUILD_END = 0.85;
      const buildP = Math.min(1, p / BUILD_END);
      targetFrameRef.current = buildP * (TOTAL_FRAMES - 1);

      if (Math.abs(p - lastP) > 0.005) {
        lastP = p;
        setProgress(p);
      }

      raf = requestAnimationFrame(updateScroll);
    };

    raf = requestAnimationFrame(updateScroll);
    return () => cancelAnimationFrame(raf);
  }, [loaded, phase]);

  useEffect(() => {
    if (!loaded) return;
    let lastDrawn = -1;
    const tick = () => {
      const cur = currentFrameRef.current;
      const tgt = targetFrameRef.current;
      // Smoothing factor: higher = canvas catches up to scroll position
      // faster. Was 0.12 (~8 frames to converge). Now 0.25 means the
      // build animation tracks the scroll without the visible lag the
      // user reported, while still smoothing over single-frame stutters.
      const next = Math.abs(tgt - cur) < 0.05 ? tgt : cur + (tgt - cur) * 0.25;
      currentFrameRef.current = next;
      const idx = Math.round(next);
      if (idx !== lastDrawn) {
        drawFrame(next);
        lastDrawn = idx;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loaded]);

  if (phase !== "PLOT") return null;

  const activeStage =
    STAGES.slice().reverse().find((s) => progress >= s.at) || STAGES[0];
  const stageIdx = STAGES.indexOf(activeStage);
  const headlineParallax = -24 * progress;
  const phaseNum = `0${stageIdx + 1}`;
  const phaseTotal = `0${STAGES.length}`;

  return (
    <section ref={sectionRef} className="construction-phase">
      <style>{`
        .construction-phase {
          position: relative;
          /* Section height = pin scroll distance (650vh) + one viewport
             (100vh) for the pin's last position. The build animation now
             completes in the first 85% of the pin distance, leaving the
             last 15% as a hold so the finished house lingers on screen
             before the reservation section appears. */
          height: 750vh;
          width: 100%;
          background: #04110a;
          z-index: 5;
        }
        .construction-pin {
          /* Native sticky pin. Global html/body uses overflow-x: clip
             (not hidden), so no ancestor scroll container exists to
             break sticky. The browser handles engage/release with no
             frame-boundary jump that the previous JS-driven
             absolute/fixed switch produced. */
          position: sticky;
          top: 0;
          height: 100vh;
          height: 100svh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, #04110a 0%, #0a0d18 50%, #04110a 100%);
          overflow: hidden;
          padding: 60px 16px;
        }
        @media (max-width: 640px) {
          .construction-pin { padding: 32px 14px; }
        }
        .construction-pin::before {
          content: "";
          position: absolute; inset: 0;
          background:
            radial-gradient(ellipse at 20% 30%, rgba(212, 164, 92,0.06), transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(212, 164, 92,0.04), transparent 50%);
          pointer-events: none;
        }
        .construction-content {
          position: relative;
          width: min(1360px, 100%);
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          align-items: center;
          z-index: 2;
          max-height: calc(100vh - 60px);
          max-height: calc(100svh - 60px);
          overflow: visible;
        }

        /* === DESKTOP / TABLET LANDSCAPE - side-by-side layout === */
        @media (min-width: 1024px) {
          .construction-content {
            grid-template-columns: 0.85fr 2fr;
            gap: 48px;
          }
          .construction-pin { padding: 0 clamp(20px, 3vw, 40px); }
        }

        /* === MOBILE - stack with video on top === */
        @media (max-width: 1023px) {
          .construction-content {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          /* Reorder: video first, text below for mobile */
          .construction-window { order: 1; }
          .construction-text   { order: 2; }
        }

        /* === LEFT SIDE === */
        .construction-text {
          display: flex; flex-direction: column;
          gap: 0;
          position: relative;
        }

        .construction-phase-counter {
          display: flex; align-items: baseline; gap: 12px;
          margin-bottom: 18px;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .construction-phase-counter__num {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-style: italic;
          font-size: clamp(24px, 4vw, 32px);
          color: #d4a45c;
          line-height: 1;
          font-feature-settings: "tnum";
          transition: transform 500ms cubic-bezier(0.4, 0, 0.2, 1);
        }
        .construction-phase-counter__divider {
          width: clamp(20px, 3vw, 28px); height: 1px;
          background: rgba(212, 164, 92,0.4);
          align-self: center;
        }
        .construction-phase-counter__total {
          font-size: clamp(9px, 1.2vw, 10px);
          letter-spacing: 0.32em;
          color: rgba(244,240,228,0.5);
          text-transform: uppercase;
        }

        .construction-text__lbl {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(9px, 1.2vw, 10px);
          letter-spacing: 0.5em;
          color: rgba(212, 164, 92,0.7); text-transform: uppercase;
          margin-bottom: 14px;
        }

        .construction-headline-wrap {
          position: relative;
          min-height: clamp(4em, 8vh, 6.4em);
          margin-bottom: 12px;
        }
        .construction-headline {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-weight: 300;
          font-size: clamp(1.4rem, 4.5vw, 2.6rem);
          color: #f4f0e4;
          letter-spacing: 0.02em;
          line-height: 1.15;
          margin: 0;
        }
        .construction-headline em {
          font-style: italic;
          background: linear-gradient(120deg, #d4a45c, #eccb92, #d4a45c);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }

        .construction-text__divider {
          width: 60px; height: 1px;
          background: linear-gradient(90deg, #d4a45c, transparent);
          margin: 4px 0 14px 0;
        }

        .construction-body-wrap {
          position: relative;
          min-height: clamp(4em, 8vh, 5.2em);
        }
        .construction-body {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-size: clamp(13px, 2vw, 15px);
          line-height: 1.6;
          color: rgba(244,240,228,0.72);
          font-style: italic;
          margin: 0;
          max-width: 420px;
        }

        .construction-stat {
          margin-top: clamp(16px, 2vh, 28px);
          padding: clamp(12px, 1.5vh, 18px) clamp(16px, 2vw, 22px);
          background: linear-gradient(135deg, rgba(212, 164, 92,0.06), rgba(212, 164, 92,0.02));
          border-left: 2px solid #d4a45c;
          border-radius: 0 6px 6px 0;
          display: flex; flex-direction: column;
          gap: 4px;
          max-width: 360px;
        }
        .construction-stat__value {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-style: italic;
          font-size: clamp(1.5rem, 3.5vw, 2.4rem);
          font-weight: 400;
          background: linear-gradient(120deg, #d4a45c, #eccb92);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          line-height: 1;
        }
        .construction-stat__caption {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(9px, 1.2vw, 10px);
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(244,240,228,0.6);
        }

        .construction-stages {
          display: flex; flex-direction: column;
          margin-top: clamp(16px, 2vh, 24px);
        }
        /* Hide stage list on small screens to save space */
        @media (max-width: 640px) {
          .construction-stages { display: none; }
        }
        .construction-stage-row {
          display: flex; align-items: center;
          gap: 14px;
          padding: 10px 0;
          border-top: 1px solid rgba(244,240,228,0.08);
          opacity: 0.32;
          transition: opacity 500ms ease, transform 500ms ease, padding-left 500ms ease;
        }
        .construction-stage-row:last-child {
          border-bottom: 1px solid rgba(244,240,228,0.08);
        }
        .construction-stage-row.is-active {
          opacity: 1;
          padding-left: 6px;
        }
        .construction-stage-row.is-done { opacity: 0.5; }
        .construction-stage-row__num {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-style: italic;
          font-size: 17px;
          color: #d4a45c;
          width: 24px;
        }
        .construction-stage-row__name {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 10px; letter-spacing: 0.32em;
          color: #f4f0e4; text-transform: uppercase;
          flex: 1;
        }
        .construction-stage-row__dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: rgba(212, 164, 92,0.2);
          transition: all 500ms ease;
        }
        .construction-stage-row.is-active .construction-stage-row__dot {
          background: #d4a45c;
          box-shadow: 0 0 14px rgba(212, 164, 92,0.85);
          transform: scale(1.4);
        }
        .construction-stage-row.is-done .construction-stage-row__dot {
          background: #d4a45c;
        }

        @keyframes morph-in {
          0%   { opacity: 0; transform: translateY(14px); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }
        .morph-key {
          animation: morph-in 700ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        /* === RIGHT SIDE - VIDEO WINDOW === */
        .construction-window {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 10;
          max-height: calc(100vh - 80px);
          max-height: calc(100svh - 80px);
          border-radius: 4px;
          overflow: hidden;
          background: #000;
          border: 1px solid rgba(212, 164, 92,0.18);
          box-shadow:
            0 30px 80px rgba(0,0,0,0.5),
            0 0 0 1px rgba(212, 164, 92,0.08),
            inset 0 0 60px rgba(0,0,0,0.4);
        }
        @media (min-width: 1024px) {
          .construction-window {
            aspect-ratio: 16 / 10;
          }
        }
        @media (max-width: 1023px) {
          .construction-window {
            aspect-ratio: 16 / 10;
          }
        }
        .construction-canvas {
          display: block;
          width: 100%;
          height: 100%;
          /* This canvas is a display-only build-progress image (role="img"),
             not an interactive/draggable surface — it has no gesture of its
             own to claim. touch-action: none here blocked the browser's
             native scroll-gesture recognition for swipes starting over the
             canvas, which is why the page would only scroll past this
             section when the touch started above/below it, never on it, on
             mobile/tablet. pan-y lets vertical swipes scroll the page
             normally, matching desktop wheel-scroll behaviour. */
          touch-action: pan-y;
        }
        .construction-window-corners {
          position: absolute; inset: 8px;
          pointer-events: none;
          z-index: 2;
        }
        .construction-window-corners::before,
        .construction-window-corners::after {
          content: "";
          position: absolute;
          width: 18px; height: 18px;
          border: 1px solid rgba(212, 164, 92,0.6);
        }
        .construction-window-corners::before {
          top: 0; left: 0;
          border-right: none; border-bottom: none;
        }
        .construction-window-corners::after {
          bottom: 0; right: 0;
          border-left: none; border-top: none;
        }

        .construction-caption {
          position: absolute; left: 0; right: 0; bottom: 0;
          padding: clamp(10px, 1.5vw, 14px) clamp(14px, 2vw, 22px);
          display: flex; align-items: center; justify-content: space-between;
          background: linear-gradient(180deg, transparent, rgba(0,0,0,0.75));
          z-index: 3;
          gap: 12px;
        }
        .construction-caption__stage {
          font-family: 'Montserrat', system-ui, sans-serif;
          font-style: italic;
          font-size: clamp(12px, 1.6vw, 15px);
          color: #f4f0e4;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .construction-caption__stage span {
          color: #d4a45c;
          margin-right: 10px;
          font-size: clamp(11px, 1.4vw, 13px);
          letter-spacing: 0.1em;
          font-style: normal;
        }
        .construction-caption__progress {
          display: flex; align-items: center; gap: 10px;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: clamp(8px, 1vw, 9px);
          letter-spacing: 0.32em;
          color: rgba(212, 164, 92,0.7); text-transform: uppercase;
          flex-shrink: 0;
        }
        .construction-caption__bar {
          width: clamp(40px, 6vw, 70px); height: 1px;
          background: rgba(212, 164, 92,0.2);
          overflow: hidden;
        }
        .construction-caption__bar-fill {
          height: 100%;
          background: #d4a45c;
          transition: width 200ms linear;
        }

        /* Hide caption description on very small screens */
        @media (max-width: 480px) {
          .construction-caption__stage { font-size: 11px; }
          .construction-caption__progress span { display: none; }
        }

        .construction-loading {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          color: rgba(212, 164, 92,0.7);
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 9px; letter-spacing: 0.4em; text-transform: uppercase;
          background: #04110a;
          z-index: 5;
        }
      `}</style>

      <div className="construction-pin">
        <div className="construction-content">
          <div
            className="construction-text"
            style={{ transform: `translateY(${headlineParallax}px)` }}
          >
            <div className="construction-phase-counter">
              <div
                key={`num-${stageIdx}`}
                className="construction-phase-counter__num morph-key"
              >
                {phaseNum}
              </div>
              <div className="construction-phase-counter__divider" />
              <div className="construction-phase-counter__total">of {phaseTotal}</div>
            </div>

            <div className="construction-text__lbl">{home.constructionLabel}</div>

            <div className="construction-headline-wrap">
              <h2 key={`h-${stageIdx}`} className="construction-headline morph-key">
                {activeStage.headline[0]} <em>{activeStage.headline[1]}</em>
              </h2>
            </div>

            <div className="construction-text__divider" />

            <div className="construction-body-wrap">
              <p key={`b-${stageIdx}`} className="construction-body morph-key">
                {activeStage.body}
              </p>
            </div>

            <div key={`s-${stageIdx}`} className="construction-stat morph-key">
              <div className="construction-stat__value">{activeStage.stat.value}</div>
              <div className="construction-stat__caption">{activeStage.stat.caption}</div>
            </div>

            <div className="construction-stages">
              {STAGES.map((s, i) => {
                const isActive = i === stageIdx;
                const isDone = i < stageIdx;
                return (
                  <div
                    key={i}
                    className={`construction-stage-row ${isActive ? "is-active" : ""} ${isDone ? "is-done" : ""}`}
                  >
                    <div className="construction-stage-row__num">0{i + 1}</div>
                    <div className="construction-stage-row__name">{s.label}</div>
                    <div className="construction-stage-row__dot" />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="construction-window">
            <canvas
              ref={canvasRef}
              className="construction-canvas"
              role="img"
              aria-label={`Construction stage: ${activeStage.label}`}
            />
            <div className="construction-window-corners" />

            <div className="construction-caption">
              <div className="construction-caption__stage">
                <span>0{stageIdx + 1}</span>
                {activeStage.desc}
              </div>
              <div className="construction-caption__progress">
                <span>{Math.round(progress * 100)}%</span>
                <div className="construction-caption__bar">
                  <div
                    className="construction-caption__bar-fill"
                    style={{ width: `${progress * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {!loaded && <div className="construction-loading">Preparing build sequence…</div>}
          </div>
        </div>
      </div>
    </section>
  );
}
